import React, { useState, useEffect, useRef } from 'react';
import { EyeOff, Lock, Shield, X } from 'lucide-react';
import { usePresentationStore } from '../../stores/presentationStore';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PRIVACY_REVEAL_EVENT } from '../../utils/privacyRevealRequest';
import toast from 'react-hot-toast';

export function PresentationModeToggle() {
    const { user } = useAuth();
    const {
        isPresentationMode,
        savedPin,
        pinLoaded,
        pinUserId,
        loadPin,
        enablePresentationMode,
        disablePresentationMode,
    } = usePresentationStore();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const desktopButtonRef = useRef<HTMLButtonElement>(null);
    const mobileButtonRef = useRef<HTMLButtonElement>(null);
    const [showModal, setShowModal] = useState(false);
    const [pin, setPin] = useState('');
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const requestedAction = isPresentationMode ? 'disable' : 'enable';

    // Load PIN from DB on mount
    useEffect(() => {
        if (user?.id && (!pinLoaded || pinUserId !== user.id)) {
            loadPin(user.id);
        }
    }, [user?.id, pinLoaded, pinUserId, loadPin]);

    const handleToggleClick = () => {
        if (!user?.id || !pinLoaded || pinUserId !== user.id) {
            toast('Presentation PIN is still loading', { icon: '🔒' });
            return;
        }
        if (!savedPin) {
            toast('Set a Presentation PIN in Settings first', { icon: '🔒' });
            navigate('/settings?panel=presentation-mode');
            return;
        }
        setPin('');
        setError('');
        setShowModal(true);
    };

    // A screen with its own privacy button asks for the PIN dialog here instead of switching privacy off itself. The header is drawn twice (phone and
    // desktop); only the copy that can be seen takes the request.
    useEffect(() => {
        const onRevealRequest = (event: Event) => {
            if (event.defaultPrevented || !isPresentationMode) return;
            const visible = [desktopButtonRef.current, mobileButtonRef.current].some((button) => button !== null && button.getClientRects().length > 0);
            if (!visible) return;
            event.preventDefault();
            handleToggleClick();
        };
        window.addEventListener(PRIVACY_REVEAL_EVENT, onRevealRequest);
        return () => window.removeEventListener(PRIVACY_REVEAL_EVENT, onRevealRequest);
    });

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        const result = requestedAction === 'enable'
            ? await enablePresentationMode(pin)
            : await disablePresentationMode(pin);
        setSubmitting(false);
        if (result === 'success') {
            // The address may still say privacy=on / display=overhead; it would mask again at once. The PIN has been given, so drop them.
            if (requestedAction === 'disable' && (searchParams.has('privacy') || searchParams.has('display'))) {
                const next = new URLSearchParams(searchParams);
                next.delete('privacy');
                next.delete('display');
                setSearchParams(next, { replace: true });
            }
            setShowModal(false);
            toast.success(`Presentation mode ${requestedAction}d`);
        } else if (result === 'incorrect') {
            setError('Incorrect PIN');
        } else {
            setError('Presentation mode could not be changed. Check your connection and try again.');
        }
    };

    return (
        <>
            <button
                ref={desktopButtonRef}
                type="button"
                onClick={handleToggleClick}
                className={`p-2 rounded-lg transition-colors relative ${isPresentationMode
                        ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                        : 'bg-white text-gray-400 border border-gray-300 shadow-sm hover:text-gray-900 lg:flex hidden'
                    }`}
                title={isPresentationMode ? "Presentation Mode Active" : "Enable Presentation Mode"}
            >
                {isPresentationMode ? <Shield className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                {isPresentationMode && (
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white"></span>
                )}
            </button>

            {/* Mobile replica */}
            <button
                ref={mobileButtonRef}
                type="button"
                onClick={handleToggleClick}
                className={`p-2 rounded-lg transition-colors relative lg:hidden ${isPresentationMode
                        ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                        : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                    }`}
                title={isPresentationMode ? "Presentation Mode Active" : "Enable Presentation Mode"}
            >
                {isPresentationMode ? <Shield className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                {isPresentationMode && (
                    <span className="absolute top-0 right-0 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white"></span>
                )}
            </button>

            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 relative">
                        <button
                            type="button"
                            onClick={() => setShowModal(false)}
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                            title="Close"
                        >
                            <X className="w-5 h-5" />
                        </button>

                        <div className="flex items-center space-x-3 mb-4">
                            <div className="p-2 rounded-lg bg-red-100 text-red-600">
                                <Lock className="w-6 h-6" />
                            </div>
                            <h2 className="text-xl font-bold text-gray-900">
                                {requestedAction === 'enable' ? 'Enable' : 'Disable'} Presentation Mode
                            </h2>
                        </div>

                        <p className="text-gray-600 mb-6 text-[0.9375rem]">
                            Enter your PIN to {requestedAction} presentation mode. This controls screen-share masking for this browser tab.
                        </p>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Enter PIN
                                </label>
                                <input
                                    type="password"
                                    value={pin}
                                    onChange={(e) => setPin(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg tracking-widest"
                                    placeholder="••••"
                                    autoFocus
                                    required
                                    disabled={submitting}
                                />
                                {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
                            </div>

                            <div className="flex justify-end space-x-3 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    disabled={submitting}
                                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 font-medium"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="px-4 py-2 text-white rounded-lg font-medium bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                                >
                                    {submitting ? 'Checking…' : requestedAction === 'enable' ? 'Enable masking' : 'Disable & reveal'}
                                </button>
                            </div>
                        </form>

                        <button
                            type="button"
                            onClick={() => {
                                setShowModal(false);
                                navigate('/settings?panel=presentation-mode');
                            }}
                            className="mt-4 text-sm font-medium text-blue-700 underline"
                        >
                            Forgot the PIN? Reset it in Settings
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}
